import { useEffect, useState } from "react";

import AdminLayout from "../../../layouts/AdminLayout";
import DataTableLayout from "../../../layouts/DataTableLayout";
import DataTable from "../../../components/common/DataTable";

import Button from "../../../components/ui/Button";
import { Plus } from "lucide-react";
import ActionButtons from "../../../components/common/ActionButtons";
import FilterBar from "../../../components/common/FilterBar";
import Modal from "../../../components/ui/Modal";
import DeleteConfirmationModal from "../../../components/common/DeleteConfirmationModal";
import { toast } from "react-toastify";
import AddAmenity from "./AddAmenity";
import AssignAmenityModal from "./AssignAmenityModal";
import AmenityBookings from "./AmenityBookings";
import { getSocieties } from "../../../services/societyService";
import { getIconComponent } from "../../../utils/IconsList";
import Skeleton from "../../../components/ui/Skeleton";

import {
  getAmenities,
  deleteAmenity,
} from "../../../services/amenityService";

const Amenities = () => {
  const [activeTab, setActiveTab] = useState("directory");
  const [amenities, setAmenities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [openModal, setOpenModal] = useState(false);
  const [selected, setSelected] = useState(null);
  const [viewMode, setViewMode] = useState(false);

  const [optionsModalOpen, setOptionsModalOpen] = useState(false);
  const [optionsModalData, setOptionsModalData] = useState(null);

  const [societies, setSocieties] = useState([]);
  const [openAssignModal, setOpenAssignModal] = useState(false);
  const [assignAmenityTarget] = useState(null);

  // 🔥 FILTER STATE
  const [filters, setFilters] = useState({
    search: "",
    society_id: "",
  });

  const [appliedFilters, setAppliedFilters] = useState({
    search: "",
    society_id: "",
  });

  // 🔹 FETCH DATA
  const fetchAmenities = async () => {
    try {
      const [amenitiesData, societiesData] = await Promise.all([
        getAmenities(),
        getSocieties()
      ]);
      setAmenities(Array.isArray(amenitiesData) ? amenitiesData : []);
      setSocieties(Array.isArray(societiesData) ? societiesData : []);
    } catch (err) {
      console.error("Error fetching amenities:", err);
      setAmenities([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAmenities();
  }, []);

  // 🔹 FILTER LOGIC
  const filteredData = (Array.isArray(amenities) ? amenities : []).filter((item) => {
    const search = (appliedFilters.search || "").toLowerCase();
    const targetSociety = appliedFilters.society_id;

    const matchesSearch = !search || (item.name || "").toLowerCase().includes(search);
    const matchesSociety = !targetSociety || String(item.society_id) === String(targetSociety);

    return matchesSearch && matchesSociety;
  });

  // 🔹 HANDLERS
  const handleAdd = () => {
    setSelected(null);
    setViewMode(false);
    setOpenModal(true);
  };

  const handleEdit = (row) => {
    setSelected(row);
    setViewMode(false);
    setOpenModal(true);
  };

  const handleView = (row) => {
    setSelected(row);
    setViewMode(true);
    setOpenModal(true);
  };

  const handleDelete = (row) => {
    setDeleteTarget({
      row,
      type: "Amenity",
      id: row.amenity_id,
      name: row.name || `Amenity #${row.amenity_id}`,
      canDelete: true,
      actionLabel: "Deactivate",
    });
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget?.row) return;
    try {
      await deleteAmenity(deleteTarget.row.amenity_id);
      toast.success("Amenity deactivated successfully.");
      fetchAmenities();
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Failed to deactivate amenity.");
    }
  };

  // 🔹 APPLY FILTER
  const handleApplyFilters = () => {
    setAppliedFilters(filters);
  };

  const handleClearFilters = () => {
    const empty = { search: "", society_id: "" };
    setFilters(empty);
    setAppliedFilters(empty);
  };

  // 🔹 COLUMNS
  const columns = [
    {
      header: "Sr No",
      render: (_, index) => index + 1,
    },
    {
      header: "Amenity",
      accessor: "name",
    },
    {
      header: "Society",
      render: (row) => societies.find(s => s.society_id === row.society_id)?.society_name || "Unknown",
    },
    {
      header: "Type",
      render: (row) => (
        <span style={{ padding: '4px 8px', backgroundColor: '#f3f4f6', color: '#374151', fontSize: '12px', borderRadius: '9999px', textTransform: 'capitalize', border: '1px solid #e5e7eb' }}>
          {row.type || "Activity"}
        </span>
      ),
    },
    {
      header: "Booking Options",
      render: (row) => {
        const opts = row.booking_options || [];
        if (!opts || opts.length === 0) return <span style={{ color: '#9ca3af', fontSize: '12px', fontStyle: 'italic' }}>None</span>;

        return (
          <div
            style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', cursor: 'pointer', position: 'relative', minWidth: '100px' }}
            onClick={(e) => {
              e.stopPropagation();
              setOptionsModalData(opts);
              setOptionsModalOpen(true);
            }}
            title="Click to view all options"
          >
            {opts.slice(0, 3).map((opt, i) => (
              <div
                key={i}
                style={{
                  width: '32px', height: '32px', borderRadius: '50%', backgroundColor: '#eff6ff',
                  border: '2px solid white', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: 'var(--color-dark-bg)', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', flexShrink: 0,
                  marginLeft: i > 0 ? '-8px' : '0', zIndex: 10 - i, fontSize: '16px'
                }}
                title={opt.label}
              >
                {getIconComponent(opt.icon)}
              </div>
            ))}
            {opts.length > 3 && (
              <div
                style={{
                  width: '32px', height: '32px', borderRadius: '50%', backgroundColor: '#f3f4f6',
                  border: '2px solid white', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#4b5563', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', flexShrink: 0,
                  marginLeft: '-8px', zIndex: 0, fontSize: '12px', fontWeight: 'bold'
                }}
                title={`${opts.length - 3} more`}
              >
                +{opts.length - 3}
              </div>
            )}
          </div>
        );
      },
    },
    {
      header: "Action",
      render: (row) => (
        <div className="flex items-center gap-2">
          <ActionButtons
            row={row}
            onEdit={handleEdit}
            onDelete={() => handleDelete(row)}
            onView={handleView}
            deleteLabel="Deactivate Amenity"
          />
        </div>
      ),
    },
  ];

  return (
    <AdminLayout>
      <div className="flex border-b border-gray-200" style={{ gap: '12px', marginBottom: '24px', paddingBottom: '16px', margin: '24px 24px 24px 24px' }}>
        <Button
          variant={activeTab === "directory" ? "primary" : "outline"}
          onClick={() => setActiveTab("directory")}
        >
          Amenities Directory
        </Button>
        <Button
          variant={activeTab === "bookings" ? "primary" : "outline"}
          onClick={() => setActiveTab("bookings")}
        >
          Booking Requests
        </Button>
      </div>

      {activeTab === "directory" ? (
        <>
          <DataTableLayout
            title="Amenities"
            filters={
              <FilterBar
                filtersConfig={[
                  {
                    key: "search",
                    label: "Search",
                    type: "text",
                    placeholder: "Search amenity...",
                  },
                  {
                    key: "society_id",
                    label: "Property",
                    type: "select",
                    options: [
                      { value: "", label: "All Properties (Master Catalog)" },
                      ...societies.map((s) => ({
                        value: String(s.society_id),
                        label: s.society_name,
                      })),
                    ],
                  },
                ]}
                filters={filters}
                setFilters={setFilters}
                onApply={handleApplyFilters}
                onClear={handleClearFilters}
              />
            }
            actions={
              <Button variant="primary" icon={<Plus size={16} />} onClick={handleAdd}>
                Add Amenity
              </Button>
            }
          >
            {loading ? (
              <Skeleton variant="list" count={3} />
            ) : (
              <DataTable columns={columns} data={filteredData} />
            )}
          </DataTableLayout>

          {/* 🔥 MODAL */}
          <Modal
            isOpen={openModal}
            onClose={() => setOpenModal(false)}
            title={
              viewMode
                ? "View Amenity"
                : selected
                  ? "Edit Amenity"
                  : "Add Amenity"
            }
          >
            <AddAmenity
              amenity={selected}
              societies={societies}
              readOnly={viewMode}
              onSuccess={() => {
                setOpenModal(false);
                fetchAmenities();
              }}
            />
          </Modal>

          <Modal
            isOpen={openAssignModal}
            onClose={() => setOpenAssignModal(false)}
            title="Assign / Book Amenity"
          >
            {assignAmenityTarget && (
              <AssignAmenityModal
                amenity={assignAmenityTarget}
                onClose={() => setOpenAssignModal(false)}
                onSuccess={() => {
                  setOpenAssignModal(false);
                  alert("Amenity assigned successfully!");
                }}
              />
            )}
          </Modal>
        </>
      ) : (
        <div className="mx-6">
          <AmenityBookings societyId={filters.society} />
        </div>
      )}

      {/* 🔥 OPTIONS MODAL */}
      <Modal
        isOpen={optionsModalOpen}
        onClose={() => setOptionsModalOpen(false)}
        title="Booking Options"
      >
        <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {optionsModalData?.length > 0 ? (
            optionsModalData.map((opt, idx) => (
              <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #f3f4f6' }}>
                <div style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-dark-bg)', fontWeight: 'bold', fontSize: '20px' }}>
                  {getIconComponent(opt.icon)}
                </div>
                <span style={{ fontWeight: '500', color: '#1f2937' }}>{opt.label}</span>
              </div>
            ))
          ) : (
            <p style={{ color: '#6b7280', fontStyle: 'italic' }}>No booking options available.</p>
          )}
        </div>
      </Modal>

      <DeleteConfirmationModal
        isOpen={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        entityType={deleteTarget?.type || "Amenity"}
        entityId={deleteTarget?.id}
        entityName={deleteTarget?.name || ""}
        canDelete={deleteTarget?.canDelete !== false}
        actionLabel={deleteTarget?.actionLabel || "Deactivate"}
        onConfirm={handleConfirmDelete}
      />
    </AdminLayout>
  );
};

export default Amenities;